import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import { Redirect, Route, Router, Switch, useLocation } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import {
  IHC_ANTIBODIES_EXTERNAL_URL,
  IHC_ANTIBODIES_INDEX_URL,
  LOGIN_URL,
} from 'constants/urls';
import { LoginPage } from 'components/Login/LoginPage';
import { onSentryRouteChange } from 'utilities/sentry';
import {
  AuthenticatedRouteDetails,
  AuthenticatedRouteDetailsKeys,
  BasicRouteDetails,
  BasicRouteDetailsKeys,
} from 'constants/routes';
import { useGetSlideGroupsQuery } from 'store/apis/slideGroupsApi';
import { getLocalToken } from 'utilities/token';
import { useGetUserDetailsQuery } from 'store/apis/userDetailsApi';
import { FETCH_TEAMS_ERROR_MESSAGE } from 'constants/errorMessages';
import { useQueryError } from 'utilities/hooks/useQueryError/useQueryError';
import { AntibodiesPage } from 'components/AntibodiesList/AntibodiesPage';
import { AntibodyIndexPage } from 'components/AntibodiesList/AntibodyIndexPage';
import { AntibodyFacetPage } from 'components/AntibodiesList/AntibodyFacetPage';
import { FACETS } from 'components/AntibodiesList/antibodyFacets';
import {
  Spinner,
  defaultSpinnerThemeStyle,
} from 'components/Loading/Spinner/Spinner';
import { useDispatch, useSelector } from 'react-redux';
import {
  setAuthToken,
  userDetailsAuthTokenSelector,
} from 'store/slices/userDetailsSlice';
import { useAuth0 } from '@auth0/auth0-react';
import { TokenFetchingPage } from 'components/Login/TokenFetchingPage';
import { ProductFruits } from 'react-product-fruits';
import { useProductFruits } from 'utilities/hooks/useProductFruits/useProductFruits';
import { useGetTeamsQuery } from 'store/apis/teamsApi';

const hist = createBrowserHistory();

// Sentry is only gated at boot, so an IF viewer opened from inside the app would
// otherwise keep the Session Replay started on an earlier, non-viewer route.
const SentryRouteWatcher = () => {
  const { pathname } = useLocation();

  // Layout effect so Replay stops before the viewer paints its first frame.
  useLayoutEffect(() => {
    onSentryRouteChange(pathname);
  }, [pathname]);

  return null;
};

const AuthenticatedRoute = ({
  component: Component,
  isLoading,
  isTokenObtaining,
  ...rest
}) => {
  const isAuthenticatedWithLocalToken = !!getLocalToken();
  const authToken = useSelector(userDetailsAuthTokenSelector);

  const isJWTFetching =
    !isAuthenticatedWithLocalToken && (isLoading || isTokenObtaining);

  return isJWTFetching ? (
    <TokenFetchingPage />
  ) : (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticatedWithLocalToken || authToken ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: LOGIN_URL,
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
};

export const RouterRoutes = () => {
  const {
    isAuthenticated: isAuthenticatedWithJWT,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const isToken = getLocalToken();

  const authToken = useSelector(userDetailsAuthTokenSelector);
  const dispatch = useDispatch();
  const isAuthenticatedWithLocalToken = !!getLocalToken();

  const {
    isLoading: isProductFruitLoading,
    userConfig,
    workspaceCode,
  } = useProductFruits();

  useGetUserDetailsQuery(undefined, { skip: !isToken && !authToken });
  const { error } = useGetTeamsQuery(undefined, {
    skip: !isToken && !authToken,
  });
  useGetSlideGroupsQuery(undefined, { skip: !isToken });
  useQueryError(error, FETCH_TEAMS_ERROR_MESSAGE);

  const isAuthenticated =
    isAuthenticatedWithLocalToken || isAuthenticatedWithJWT;

  const isTokenObtaining = isAuthenticatedWithJWT && !authToken;

  const getJWT = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        aud: 'django-backend',
      });

      dispatch(setAuthToken(accessToken));
    } catch (e) {
      // There are cases when it's an expected behaviour so no need to show a toast
      // eslint-disable-next-line no-console
      console.log(e);
    }
  }, [dispatch, getAccessTokenSilently]);

  useEffect(() => {
    if (authToken || isAuthenticatedWithLocalToken) return;

    getJWT();
  }, [authToken, getJWT, isAuthenticatedWithLocalToken]);

  return (
    <Router history={hist}>
      <SentryRouteWatcher />
      {!isProductFruitLoading && workspaceCode?.length && (
        <ProductFruits
          workspaceCode={workspaceCode}
          user={userConfig}
          language="en"
          // lifeCycle="unmount" TODO: investigate if this prop affects performance
        />
      )}
      {/* Some routes are lazy (the IF viewer and the pages that render it), so the switch
          needs a Suspense boundary for the chunk to load behind. */}
      <Suspense
        fallback={
          <div style={defaultSpinnerThemeStyle}>
            <Spinner />
          </div>
        }
      >
        <Switch>
          {BasicRouteDetailsKeys.map((path) => {
            const component = BasicRouteDetails[path];

            return <Route exact path={path} component={component} key={path} />;
          })}
          {AuthenticatedRouteDetailsKeys.map((path) => {
            const component = AuthenticatedRouteDetails[path];

            return (
              <AuthenticatedRoute
                exact
                path={path}
                component={component}
                key={path}
                isAuthenticated={isAuthenticated}
                isLoading={isLoading}
                isTokenObtaining={isTokenObtaining}
                isAuthenticatedWithLocalToken={isAuthenticatedWithLocalToken}
              />
            );
          })}
          {/* These must precede the antibodies route: its optional :antibodyUUID param
            matches a prefix and would otherwise swallow every /ihc-antibodies/... path. */}
          <Route
            exact
            path={IHC_ANTIBODIES_INDEX_URL}
            component={AntibodyIndexPage}
          />
          {FACETS.map((facet) => (
            <Route
              key={facet.segment}
              exact
              path={facet.route}
              render={() => <AntibodyFacetPage facet={facet} />}
            />
          ))}
          <Route
            exact
            path={IHC_ANTIBODIES_EXTERNAL_URL}
            component={AntibodiesPage}
          />
          <Route path="/" component={LoginPage} />
        </Switch>
      </Suspense>
    </Router>
  );
};
